import { ClockIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatTimeValue, parseTimeValue } from "@/lib/utils/dateUtils";

interface CustomTimePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
  minuteStep?: number;
}

export const CustomTimePicker = ({
  value = "",
  onChange,
  placeholder = "HH:MM",
  className,
  triggerClassName,
  id,
  ariaLabel,
  disabled = false,
  minuteStep = 1,
}: CustomTimePickerProps) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const parsedValue = useMemo(() => parseTimeValue(value), [value]);
  const [selectedHour, setSelectedHour] = useState<number | null>(
    parsedValue.hour,
  );
  const [selectedMinute, setSelectedMinute] = useState<number | null>(
    parsedValue.minute,
  );

  useEffect(() => {
    setSelectedHour(parsedValue.hour);
    setSelectedMinute(parsedValue.minute);
  }, [parsedValue.hour, parsedValue.minute]);

  const hours = useMemo(
    () => Array.from({ length: 24 }, (_, index) => index),
    [],
  );
  const minutes = useMemo(() => {
    const step = minuteStep > 0 ? minuteStep : 1;
    const count = Math.ceil(60 / step);
    return Array.from({ length: count }, (_, index) => index * step);
  }, [minuteStep]);

  const displayValue =
    selectedHour != null && selectedMinute != null
      ? formatTimeValue(selectedHour, selectedMinute)
      : "";
  const popoverContainer =
    triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? undefined;

  const handleHourSelect = (hour: number) => {
    setSelectedHour(hour);
    if (selectedMinute != null) {
      onChange(formatTimeValue(hour, selectedMinute));
    }
  };

  const handleMinuteSelect = (minute: number) => {
    setSelectedMinute(minute);
    if (selectedHour != null) {
      onChange(formatTimeValue(selectedHour, minute));
    }
  };

  return (
    <div className={cn("relative", className)}>
      <Popover open={isOpen && !disabled} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            id={id}
            aria-label={ariaLabel || placeholder || "Select time"}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            variant="outline"
            className={cn("w-full justify-start font-normal", triggerClassName)}
            disabled={disabled}
          >
            <ClockIcon className="mr-2 size-4" />
            {displayValue || placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-64 p-3"
          align="start"
          container={popoverContainer}
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground">
                {t("common.hour", { count: 1 })}
              </div>
              <div className="max-h-48 overflow-y-auto rounded-md border">
                {hours.map((hour) => {
                  const isSelected = selectedHour === hour;
                  return (
                    <Button
                      key={hour}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "w-full justify-start rounded-none px-2",
                        isSelected &&
                          "bg-rw-main text-white hover:bg-rw-main-hover",
                      )}
                      onClick={() => handleHourSelect(hour)}
                    >
                      {String(hour).padStart(2, "0")}
                    </Button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground">
                {t("common.minute", { count: 1 })}
              </div>
              <div className="max-h-48 overflow-y-auto rounded-md border">
                {minutes.map((minute) => {
                  const isSelected = selectedMinute === minute;
                  return (
                    <Button
                      key={minute}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "w-full justify-start rounded-none px-2",
                        isSelected &&
                          "bg-rw-main text-white hover:bg-rw-main-hover",
                      )}
                      onClick={() => handleMinuteSelect(minute)}
                    >
                      {String(minute).padStart(2, "0")}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
